import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import Brand from "@/components/Brand";
import Spinner from "@/components/Spinner";

/**
 * Shared login form used for both super_admin and school_admin.
 * `expectedRole` decides where to redirect after a successful login;
 * if the returned role doesn't match, we still route based on the
 * real role the backend gave us.
 */
export default function AdminLogin({ expectedRole = "super_admin" }) {
  const { loginAdmin } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const heading =
    expectedRole === "super_admin" ? "Super Admin Login" : "School Admin Login";
  const altLink =
    expectedRole === "super_admin"
      ? { to: "/school/login", text: "School admin?" }
      : { to: "/admin/login", text: "Super admin?" };

  async function onSubmit(e) {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const res = await loginAdmin(email.trim().toLowerCase(), password);
    setSubmitting(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    if (res.role === "super_admin") navigate("/admin", { replace: true });
    else if (res.role === "school_admin") navigate("/school", { replace: true });
    else setError(`Unknown role: ${res.role}`);
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex flex-col items-center justify-center p-6">
      <Brand size="lg" className="mb-6" />
      <div className="w-full max-w-sm bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
        <h1 className="text-xl font-semibold text-slate-900 mb-1">{heading}</h1>
        <p className="text-sm text-slate-500 mb-6">
          Sign in with your email and password.
        </p>

        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Email
            </label>
            <input
              type="email"
              required
              autoFocus
              data-testid="admin-login-email"
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Password
            </label>
            <input
              type="password"
              required
              data-testid="admin-login-password"
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>

          {error ? (
            <p data-testid="admin-login-error" className="text-sm text-red-600">
              {error}
            </p>
          ) : null}

          <button
            type="submit"
            disabled={submitting}
            data-testid="admin-login-submit"
            className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-indigo-700 text-white text-sm font-medium py-2 hover:bg-indigo-800 disabled:opacity-60 transition"
          >
            {submitting ? <Spinner className="h-4 w-4" /> : null}
            {submitting ? "Signing in…" : "Sign in"}
          </button>
        </form>

        <div className="flex justify-between mt-6 text-xs text-slate-500">
          <Link to="/" className="hover:text-slate-900">← Home</Link>
          <Link to={altLink.to} className="hover:text-slate-900">
            {altLink.text}
          </Link>
        </div>
      </div>
    </div>
  );
}
